import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDownloadVersion } from "../hooks/useDownloadVersion";
import { useAuth } from "../hooks/useAuth";
import { useIsHandheld } from "../hooks/useIsHandheld";
import TrialPanel from "./TrialPanel";
import LeadCapturePanel from "./LeadCapturePanel";
import { trackEvent } from "../lib/analytics";

const MAC_URL = "/api/download/mac";
const WIN_URL = "/api/download/windows";

export default function Download() {
  const { t } = useTranslation("landing");
  const versionInfo = useDownloadVersion();
  const { user, loading } = useAuth();
  const isHandheld = useIsHandheld();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#A1A1AA]">{t("download.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="mx-auto max-w-2xl px-6 py-12 sm:py-16">
        <Link
          to="/"
          className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] hover:text-[#FF5500]"
        >
          {t("download.back")}
        </Link>

        <header className="mt-8 mb-10">
          <h1 className="font-clash text-4xl font-bold sm:text-5xl">
            {t("download.heading")}
          </h1>
          <p className="mt-3 text-[#A1A1AA] text-lg">
            {t("download.subheading")}
          </p>
          {versionInfo?.version && (
            <p className="mt-2 font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">
              {t("download.version", { version: versionInfo.version })}
            </p>
          )}
        </header>

        {!user ? (
          <div className="feature-card rounded-lg p-8 text-center">
            <h2 className="font-clash text-2xl text-white">
              {t("download.signup.heading")}
            </h2>
            <p className="mt-3 text-[#A1A1AA]">
              {t("download.signup.body")}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/signup"
                className="btn-primary px-8 py-3 rounded text-sm font-semibold"
              >
                {t("download.signup.createAccount")}
              </Link>
              <Link
                to="/login"
                className="px-8 py-3 rounded border border-white/10 text-white text-sm font-medium hover:bg-white/5 transition-colors"
              >
                {t("download.signup.login")}
              </Link>
            </div>

            {isHandheld && (
              <LeadCapturePanel source="download_wall" />
            )}
          </div>
        ) : (
          <>
            <TrialPanel />

            <div className="feature-card rounded-lg p-8 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="font-clash text-xl text-white">{t("download.mac.title")}</h2>
                  <p className="text-sm text-[#A1A1AA] mt-1">
                    {t("download.mac.desc")}
                  </p>
                </div>
                <a
                  href={MAC_URL}
                  onClick={() => trackEvent("file_download", { platform: "mac", page: "download" })}
                  className="btn-primary px-8 py-3 rounded text-sm font-semibold"
                >
                  {t("download.mac.cta")}
                </a>
              </div>
            </div>

            <div className="feature-card rounded-lg p-8 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="font-clash text-xl text-white">{t("download.win.title")}</h2>
                  <p className="text-sm text-[#A1A1AA] mt-1">
                    {t("download.win.desc")}
                  </p>
                </div>
                <a
                  href={WIN_URL}
                  onClick={() => trackEvent("file_download", { platform: "windows", page: "download" })}
                  className="btn-primary px-8 py-3 rounded text-sm font-semibold"
                >
                  {t("download.win.cta")}
                </a>
              </div>
            </div>

            <div className="feature-card rounded-lg p-6 space-y-3">
              <h3 className="font-clash text-lg text-white">{t("download.afterInstall.heading")}</h3>
              <ol className="list-decimal pl-5 text-sm text-[#A1A1AA] space-y-2">
                <li>{t("download.afterInstall.step1")}</li>
                <li>
                  {t("download.afterInstall.step2")}
                </li>
                <li>
                  {t("download.afterInstall.step3a")}{" "}
                  <span className="text-white">{t("download.afterInstall.buy")}</span> {t("download.afterInstall.step3b")}
                </li>
              </ol>
            </div>
          </>
        )}

        <div className="mt-10 text-center text-sm text-[#A1A1AA]">
          {t("download.haveLicence")}{" "}
          <Link to="/login" className="text-[#FF5500] hover:text-[#FF7733]">
            {t("download.loginLink")}
          </Link>
        </div>
      </div>
    </div>
  );
}
